'use client';

import { BoltIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import Button from 'components/common/Button';
import Href from 'components/common/Href';
import { usePremiumEntitlements } from 'lib/hooks/premium/usePremiumEntitlements';
import { useTranslations } from 'next-intl';
import { useConnection } from 'wagmi';

interface Props {
  variant?: 'premium' | 'ultimate';
}

const PremiumExploitCta = ({ variant = 'premium' }: Props) => {
  const t = useTranslations();
  const { address: account } = useConnection();
  const { isPremium, isUltimate } = usePremiumEntitlements(account);

  // Don't advertise a tier the connected wallet already has
  if (variant === 'premium' && isPremium) return null;
  if (variant === 'ultimate' && isUltimate) return null;

  if (variant === 'ultimate') {
    return (
      <div className="rounded-xl border border-brand/40 bg-brand/5 dark:bg-brand/10 p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <BoltIcon className="h-10 w-10 shrink-0 text-brand" />
          <div className="flex flex-col gap-1">
            <h3 className="text-lg font-bold">{t('exploits.premium.ultimate_cta.title')}</h3>
            <p className="text-sm text-zinc-600 dark:text-zinc-400">{t('exploits.premium.ultimate_cta.description')}</p>
            <Href href="/premium/automated-revoking" router underline="always" className="text-sm w-fit">
              {t('exploits.premium.ultimate_cta.learn_more')} →
            </Href>
          </div>
        </div>
        <Button href="/premium" router style="primary" size="md" className="shrink-0 w-full sm:w-auto justify-center">
          {t('common.buttons.get_ultimate')}
        </Button>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-brand/40 bg-brand/5 dark:bg-brand/10 p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
      <div className="flex items-center gap-4">
        <ShieldCheckIcon className="h-10 w-10 shrink-0 text-brand" />
        <div>
          <h3 className="text-lg font-bold">{t('exploits.premium.upgrade_cta.title')}</h3>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">{t('exploits.premium.upgrade_cta.description')}</p>
        </div>
      </div>
      <Button href="/premium" router style="primary" size="md" className="shrink-0 w-full sm:w-auto justify-center">
        {t('common.buttons.upgrade_to_premium')}
      </Button>
    </div>
  );
};

export default PremiumExploitCta;
